import type { Tenant, AreasComarcales } from '@/types/types';
import { applyUserTranslationsAllTypes } from '@/lib/utils';
import { getTenantEntityFromRaw, normalizeAreasComarcales, normalizeMunicipios, normalizeAmbitosIntervencion } from './auth.utils';
import { User } from '@/types/models/user';

export interface AuthDerivedState {
  currentTenantId: number | null;
  currentTenant: Tenant | null;
  availableTenantIds: number[];
  instanceName: string | null;
  userLang: string;
  areasComarcales: AreasComarcales[];
  municipios: any[];
  ambitosIntervencion: any[];
  isAdmin: boolean;
  isManager: boolean;
  canEditFeder: boolean;
  canManageFeder: boolean;
  canEditDocuments: boolean;
  canDeleteDocuments: boolean;
}

export function getAuthDerivedState(user: User | null): AuthDerivedState {
  if (!user) {
    return {
      currentTenantId: null,
      currentTenant: null,
      availableTenantIds: [],
      instanceName: null,
      userLang: 'es-ES',
      areasComarcales: [],
      municipios: [],
      ambitosIntervencion: [],
      isAdmin: false,
      isManager: false,
      canEditFeder: false,
      canManageFeder: false,
      canEditDocuments: false,
      canDeleteDocuments: false,
    };
  }

  const tenantEntities: Tenant[] = Array.isArray(user.tenants)
    ? user.tenants
        .map(getTenantEntityFromRaw)
        .filter((t): t is Tenant => t !== null)
    : [];

  const activeTenantObj =
    typeof user.active_tenant === 'object' ? (user.active_tenant as Tenant) : null;

  const baseTenant = activeTenantObj ?? tenantEntities[0] ?? null;
  const currentTenantId = baseTenant?.id ?? null;
  const availableTenantIds = tenantEntities.map((t) => t.id);
  const userLang = user.title ?? 'es-ES';

  const localizedTenant = baseTenant
    ? applyUserTranslationsAllTypes<Tenant>([baseTenant], userLang, ['nombre'])[0]
    : null;

  const instanceName =
    localizedTenant?.nombre ??
    (typeof user.title === 'string'
      ? user.title
      : (user.title as any)?.[userLang]) ??
    null;

  const role = user?.role?.name?.trim().toLowerCase?.() ?? '';
  const isAdmin = role === 'admin' || role === 'administrator' || role.includes('administrador');
  const isManager = role === 'manager';
  const isManagerPai =
    role === 'manager pai' ||
    role === 'manager_pai' ||
    role === 'manager-pai';

  const canEditFeder = isAdmin || isManager || isManagerPai;
  const canManageFeder = isAdmin || isManagerPai;
  const canEditDocuments = isAdmin || isManager || isManagerPai;
  const canDeleteDocuments = isAdmin || isManagerPai;

  return {
    currentTenantId,
    currentTenant: localizedTenant,
    availableTenantIds,
    instanceName,
    userLang,
    areasComarcales: normalizeAreasComarcales(localizedTenant?.areas_comarcales),
    municipios: normalizeMunicipios(localizedTenant?.municipios),
    ambitosIntervencion: normalizeAmbitosIntervencion(
      localizedTenant?.ambitos_intervencion
    ),
    isAdmin,
    isManager,
    canEditFeder,
    canManageFeder,
    canEditDocuments,
    canDeleteDocuments,
  };
}
